import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import MainLayout from "../components/layout/MainLayout";
import PatientsList from "./Patient/PatientsList";
import {
  fetchPatients,
  deletePatient,
  clearError,
} from "../store/slices/patientsSlice";
import Breadcrumbs from "../components/common/Breadcrumbs";
import { selectHasPermission } from "../store/slices/authSlice";
import Swal from 'sweetalert2';


const Patients = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { patients, isLoading, error } = useSelector((state) => state.patients);

  // ✅ Permission checks
  const canManagePatients = useSelector((state) =>
    selectHasPermission(state, "patients-manage")
  );
  const canCreatePatients = useSelector((state) =>
    selectHasPermission(state, "patients-create")
  );
  const canEditPatients = useSelector((state) =>
    selectHasPermission(state, "patients-edit")
  );
  const canDeletePatients = useSelector((state) =>
    selectHasPermission(state, "patients-delete")
  );
  const canManagePatientsProfile = useSelector((state) =>
    selectHasPermission(state, "patients-profile-manage")
  );

  // ✅ Fetch data on load
  useEffect(() => {
    if (canManagePatients) {
      dispatch(fetchPatients());
    }
  }, [dispatch, canManagePatients]);

  // ✅ Handle error logging
  useEffect(() => {
    if (error) console.error("Patients error:", error);
  }, [error]);

  // ✅ No permission redirect
  if (!canManagePatients) {
    return (
      <Navigate
        to="/dashboard"
        state={{ error: "You do not have permission to manage patients." }}
        replace
      />
    );
  }

  // ✅ Action Handlers
  const handleAddPatient = () => navigate("/patients/add");
  const handleEditPatient = (id) => navigate(`/patients/edit/${id}`);
  const handleManagePatient = (patient) => {
    navigate(`/patients/manage/${patient.id}`, { state: { patientData: patient } });
  };
  const handleViewPatient = (id) => navigate(`/patients/view/${id}`);

  const handleDeletePatient = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You want to delete this patient?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deletePatient(id)).unwrap();
        dispatch(fetchPatients());
        Swal.fire('Deleted!', 'Patient has been deleted.', 'success');
      } catch (err) {
        console.error("Failed to delete patient:", err);
        Swal.fire('Error!', 'Failed to delete patient.', 'error');
      }
    }
  };


  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />

        {/* ===== Error Alert ===== */}
        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show"
            role="alert"
          >
            <strong>Error:</strong> {error}
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => dispatch(clearError())}
            ></button>
          </div>
        )}

        {/* ===== Patients Table ===== */}
        <div className="card">
          <div className="card-body">
            {isLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <PatientsList
                patients={patients}
                onEdit={canEditPatients ? handleEditPatient : null}
                onDelete={canDeletePatients ? handleDeletePatient : null}
                onView={handleViewPatient}
                onAddPatient={canCreatePatients ? handleAddPatient : null}
                onManagePatientsProfile={canManagePatientsProfile ? handleManagePatient : null}
              />
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Patients;
